import React, { FC, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Briefcase,
  Copy,
  Edit3,
  Key,
  Lock,
  RefreshCw,
  User,
} from 'react-feather';

import { UserRole } from '../../types/user.types';

import { Nullable } from '../../types';
import { notifyMsg } from '../../configs';
import { useSnack } from '../../util/useSnack';
import { DialogModal } from '../../components';
import { metaTitle } from '../../util/tabTitle';
import { useAuth } from '../../context/auth-context';
import Breadcrumbs from '../../components/Breadcrumb';
import { ProfileUpdateForm } from './ProfileUpdateForm';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useUserRoleName } from '../../util/useUserTypes';
import { useUpdateOrgApiToken } from '../../api-http/organizations';
import { ProfileChangePasswordForm } from './ProfileChangePasswordForm';
import { ManagementPageHeader } from '../../components/ManagementPageLayout';

const Detail = ({ label, value }: { label: string; value?: string }) => (
  <Box>
    <Typography
      sx={{ color: '#64748b', fontSize: 12, fontWeight: 700, mb: 0.5 }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: '#0f172a', fontSize: 14, fontWeight: 500 }}>
      {value || '—'}
    </Typography>
  </Box>
);

const sectionSx = {
  p: { xs: 2.5, sm: 3 },
  border: '1px solid #e2e8f0',
  borderRadius: 3,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
};

const ProfilePage: FC = () => {
  metaTitle(' Profile');

  const { showSuccessSnack, showErrSnack } = useSnack();

  const { currentUser, refetchMe } = useAuth();
  const disableEdit = useIsUserRole([UserRole.user]);

  const initOrgApiToken = currentUser?.organization?.orgApiToken || null;

  const [orgApiToken, setOrgApiToken] =
    useState<Nullable<string>>(initOrgApiToken);
  const [userUpdateModal, setUserUpdateModal] = useState(false);
  const [userChangePasswordModal, setUserChangePasswordModal] = useState(false);

  const { mutate } = useUpdateOrgApiToken({
    onSuccess: (res) => {
      setOrgApiToken(res?.orgApiToken);
      refetchMe();
      showSuccessSnack(notifyMsg.NEW_TOKEN_REGENERATED_SUCCESS);
    },
    onError: (err) => showErrSnack(err),
  });

  useEffect(() => {
    setOrgApiToken(initOrgApiToken);
  }, [initOrgApiToken]);

  const handleCopy = () => {
    if (typeof orgApiToken === 'string') {
      navigator.clipboard.writeText(orgApiToken);
      showSuccessSnack(notifyMsg.TOKEN_COPIED_SUCCESS);
    }
  };

  const handleRegenerate = () => {
    mutate('');
  };

  const onFinishProfileUpdate = () => {
    setUserUpdateModal(false);
    refetchMe();
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs items={[{ label: 'Profile', to: '/profile' }]} />
      <ManagementPageHeader
        title="Organization profile"
        subtitle="Manage your account, organization details, and security settings."
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
          gap: 2,
        }}
      >
        <Paper elevation={0} sx={sectionSx}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2.5}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <User size={19} color="#2563eb" />
              <Typography
                component="h2"
                sx={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}
              >
                Account details
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<Edit3 size={16} />}
              disabled={disableEdit}
              onClick={() => setUserUpdateModal(true)}
            >
              Edit profile
            </Button>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <Detail label="Name" value={currentUser?.name} />
            <Detail label="Email" value={currentUser?.email} />
            <Box>
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: 12,
                  fontWeight: 700,
                  mb: 0.75,
                }}
              >
                Role
              </Typography>
              <Chip
                size="small"
                label={useUserRoleName(currentUser?.role || '')}
                sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 700 }}
              />
            </Box>
          </Box>
          <Divider sx={{ my: 2.5 }} />
          <Button
            startIcon={<Lock size={16} />}
            onClick={() => setUserChangePasswordModal(true)}
          >
            Change password
          </Button>
        </Paper>

        <Paper elevation={0} sx={sectionSx}>
          <Stack direction="row" spacing={1.25} alignItems="center" mb={2.5}>
            <Briefcase size={19} color="#2563eb" />
            <Typography
              component="h2"
              sx={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}
            >
              Organization
            </Typography>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
              mb: 2.5,
            }}
          >
            <Detail
              label="Organization"
              value={currentUser?.organization.name}
            />
            <Detail
              label="Codename"
              value={currentUser?.organization.pinCode}
            />
          </Box>
          <Divider sx={{ mb: 2.5 }} />
          <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
            <Key size={17} color="#64748b" />
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}
            >
              Organization API token
            </Typography>
          </Stack>
          {orgApiToken === null ? (
            <Button
              variant="contained"
              startIcon={<Key size={16} />}
              onClick={handleRegenerate}
            >
              Create API token
            </Button>
          ) : (
            <TextField
              fullWidth
              size="small"
              disabled
              value={orgApiToken}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy token">
                      <IconButton
                        aria-label="Copy API token"
                        onClick={handleCopy}
                        size="small"
                      >
                        <Copy size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Regenerate token">
                      <IconButton
                        aria-label="Regenerate API token"
                        onClick={handleRegenerate}
                        size="small"
                      >
                        <RefreshCw size={16} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Paper>
      </Box>

      <DialogModal
        onClose={() => setUserUpdateModal(false)}
        open={userUpdateModal}
        title={'Edit profile'}
      >
        <ProfileUpdateForm
          profileData={currentUser}
          onFinished={onFinishProfileUpdate}
        />
      </DialogModal>
      <DialogModal
        onClose={() => setUserChangePasswordModal(false)}
        open={userChangePasswordModal}
        title={'Change password'}
      >
        <ProfileChangePasswordForm
          onFinished={() => setUserChangePasswordModal(false)}
        />
      </DialogModal>
    </Box>
  );
};

export default ProfilePage;
